import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { SalesShell } from "@/components/crm/SalesShell";
import { ImportHistoryPanel } from "@/components/crm/import/ImportHistoryPanel";
import { ImportWizard } from "@/components/crm/import/ImportWizard";
import { EmptyState } from "@/components/crm/parts";
import { useAccounts, useContracts, useStages } from "@/lib/crm/useCrm";
import { usePermissions } from "@/lib/crm/usePermissions";
import type { StageOption } from "@/lib/crm/import/stage-map";
import type { ExistingContract } from "@/lib/crm/import/validate";

export const Route = createFileRoute("/_authenticated/sales/import")({
  head: () => ({
    meta: [
      { title: "Import CRM records — MyPriorities" },
      {
        name: "description",
        content:
          "Bring existing customers and contracts in from a spreadsheet. Map, validate and review every row before anything is created.",
      },
      { property: "og:title", content: "Import CRM records — MyPriorities" },
      {
        property: "og:description",
        content: "Upload, map, validate, review — then confirm. Nothing is created until you say so.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SalesImportPage,
});

function SalesImportPage() {
  return (
    <SalesShell
      title="Import CRM records"
      subtitle="Upload a spreadsheet of existing contracts, check every row, then confirm."
    >
      {({ orgId }) => <ImportBody orgId={orgId} />}
    </SalesShell>
  );
}

function ImportBody({ orgId }: { orgId: string }) {
  const { can, ready } = usePermissions();
  const contracts = useContracts(orgId);
  const accounts = useAccounts(orgId);
  const stages = useStages(orgId);

  const existing = useMemo<ExistingContract[]>(() => {
    const names = new Map((accounts.data ?? []).map((a) => [a.id, a.name]));
    return (contracts.data ?? []).map((c) => ({
      customer: (c.account_id ? names.get(c.account_id) : null) ?? c.name,
      product: c.product_name ?? "",
      endDate: c.end_date,
    }));
  }, [contracts.data, accounts.data]);

  const stageOptions = useMemo<StageOption[]>(
    () =>
      (stages.data ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        probability: Number(s.probability ?? 0),
      })),
    [stages.data],
  );

  if (ready && !can("import_data")) {
    return (
      <EmptyState
        title="You don't have import access"
        body="Bulk import is restricted. Ask an admin to switch on “Import CRM records” for your access level in Sales settings."
      />
    );
  }

  return (
    <div className="grid fill-track gap-5">
      <ImportWizard orgId={orgId} existing={existing} stages={stageOptions} />
      <ImportHistoryPanel orgId={orgId} />
    </div>
  );
}
